import React, {useEffect, useState} from "react";
import {Alert, Card, CardBody, Col, Row} from "reactstrap";
import NTMTable from "../../components/Table/NTMTable";
import EditUserModal from "../Users/EditUserModal";
import {Link} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {updateUser} from "../../redux/users/userActions";
import NTMButton from "../../components/Buttons";
import NTMFormModal from "../../components/Modal/NTMFormModal";
import {useQuery, useQueryClient} from "react-query";
import {BANK_ACCOUNTS, Client, SHOPS} from "../../redux/utils/Client";
import {ConfirmDialog} from "@nextome/messages";
import {MoneyCell, UserCell} from "../../components/Table/tableCells";
import CSVExporter from "../../components/Table/CSVExporter";
import {useSnackbar} from "notistack";

let client = new Client();

const PAGE_SIZE = 20;
const EXPORT_PAGE_SIZE = 200;
const PAGE_SIZE_OPTIONS = [5, 20, 50, 100, 200];
const EXTERNAL_FUND_TYPE = 1001; // Fondo esterno, da escludere dall'elenco dei negozi partner.
const EMPTY_SHOP_PAGE = {
	content: [],
	totalPages: 0,
	totals: {totInvoices: 0, totOpenInvoices: 0}
};

function getShopPageParams(state, page = state.page, size = state.pageSize) {
	const filters = state.filtered.reduce((params, filter) => {
		const value = filter.value == null ? "" : String(filter.value).trim();
		if (value) params[filter.id === "id" ? "partner" : filter.id] = value;
		return params;
	}, {});
	const sort = state.sorted[0];

	return {
		...filters,
		excludeType: EXTERNAL_FUND_TYPE,
		page,
		size,
		sortBy: sort ? sort.id : "name",
		desc: sort ? sort.desc : false
	};
}

function validateShopPage(data) {
	if (!data || !Array.isArray(data.content) || !Number.isInteger(data.totalPages) ||
		!data.totals || !Number.isFinite(Number(data.totals.totInvoices)) ||
		!Number.isFinite(Number(data.totals.totOpenInvoices))) {
		throw new Error("Risposta non valida dall'endpoint di paginazione dei negozi.");
	}
	return data;
}

function formatTotal(value) {
	const total = Number(value);
	return Number.isFinite(total) ? `${total.toFixed(2)}€` : "-";
}

export default function CardPage() {

	let [modal, setModal] = useState();
	let [tableState, setTableState] = useState({
		page: 0,
		pageSize: PAGE_SIZE,
		filtered: [],
		sorted: []
	});
	let [requestState, setRequestState] = useState(tableState);
	let users = useSelector(store => store.users.list);
	const {enqueueSnackbar} = useSnackbar();
	let queryClient = useQueryClient();

	let {data: list} = useQuery("bank-accounts", () => client.get(BANK_ACCOUNTS).then(response => response.data));
	let {
		data: shopPage = EMPTY_SHOP_PAGE,
		isLoading: isShopsLoading,
		isFetching: isShopsFetching,
		isError: isShopsError,
		error: shopsError,
		refetch: refetchShops
	} = useQuery(
		["shops-page", requestState],
		() => client.get(`${SHOPS}/page`, "", {
			timeout: 30000,
			params: getShopPageParams(requestState)
		}).then(response => validateShopPage(response.data)),
		{retry: false, keepPreviousData: true}
	);
	let dispatch = useDispatch();

	useEffect(() => {
		const timeout = setTimeout(() => setRequestState(tableState), 300);
		return () => clearTimeout(timeout);
	}, [tableState]);

	let closeModal = () => setModal(null);

	function manageError(error) {
		enqueueSnackbar(client.getErrorMessage(error), {variant: "error"});
	}

	async function shopsToCsv() {
		const params = getShopPageParams(tableState, 0, EXPORT_PAGE_SIZE);
		const firstPageResponse = await client.get(`${SHOPS}/page`, "", {
			timeout: 30000,
			params
		});
		const firstPage = validateShopPage(firstPageResponse.data);
		let shops = firstPage.content.slice();

		for (let page = 1; page < firstPage.totalPages; page++) {
			const response = await client.get(`${SHOPS}/page`, "", {
				timeout: 30000,
				params: {...params, page}
			});
			shops = shops.concat(validateShopPage(response.data).content);
		}

		return shops.map(shop => {
			let user = users.find(u => u.partner_venue != null && String(u.partner_venue) === String(shop.id));
			let username = user ? `${user.firstName} ${user.lastName}` : "Sconosciuto";

			return {
				'Nome': shop.name,
				'Città': shop.city,
				'Indirizzo': shop.address,
				'Partner': username,
				'Fondo': shop.fundName,
				'Totale fatturato': shop.totInvoices,
				'Totale erogato': shop.totOpenInvoices
			};
		});
	}

	let edit = shop => setModal(
		<NTMFormModal
			initialValues={{fund: shop.fundId}}
			formData={[
				{
					type: "select",
					name: "fund",
					label: "Fondo associato",
					options: [
						{
							name: "Tucum",
							value: -1
						},
					].concat(list.map(ba => {
						return {name: `${ba.header} - ${ba.description}`, value: ba.id};
					}))
				},
			]}
			onSubmit={values => {

				setModal(<ConfirmDialog
					title={"Confermi?"}
					message={values.fund > 0 ? `Stai associando il negozio ${shop.name} al fondo ${list.find(b => b.id === values.fund).header}` : `Stai riassegnando il negozio ${shop.name} al Progetto Tucum`}
					onCancel={setModal(null)}
					onConfirm={() => {
						let request = values.fund > 0
							? client.update({}, `shops/${shop.id}/fund/${values.fund}`)
							: client.delete({id: ""}, `shops/${shop.id}/fund`);
						request
							.then(() => queryClient.invalidateQueries("shops-page"))
							.catch(manageError);
						setModal(null);
					}}
				/>);
			}}
			onClose={closeModal}
			title={"Associa fondo"}
		/>
	);

	const shopTotals = shopPage.totals || {};

	return (
		<div className="animated fadeIn">
			{modal}
			{isShopsError && <Alert color="danger" role="alert">
				Impossibile caricare i negozi: {client.getErrorMessage(shopsError)}{" "}
				<NTMButton primary onClick={() => refetchShops()}>Riprova</NTMButton>
			</Alert>}
			<Row>
				<Col>
					<Card>
						<CardBody>
							<Row>
								<Col>Totale fatturato selezione: {isShopsLoading ? "Caricamento..." : formatTotal(shopTotals.totInvoices)}</Col>
								<Col>Totale erogato selezione: {isShopsLoading ? "Caricamento..." : formatTotal(shopTotals.totOpenInvoices)}</Col>
							</Row>
						</CardBody>
					</Card>

				</Col>
			</Row>
			<Row><CSVExporter name={"negozi"} data={shopsToCsv} onError={manageError}/></Row>
			<Row>
				<Col>
					<NTMTable
						title={"Negozi Partner"}
						header={[
							{
								Header: 'Nome',
								accessor: 'name' // String-based value accessors!
							},
							{
								Header: 'Città',
								accessor: 'city',
							},
							{
								Header: 'Indirizzo',
								accessor: 'address',
							},
							{
								Header: 'Partner',
								accessor: "id",
								Cell: props => <UserCell
									user={users.find(u => u.partner_venue === props.original.id)}
									onClick={() => {
										setModal(
											<EditUserModal
												user={users.find(u => u.partner_venue === props.original.id)}
												onSubmit={(values) => {
													dispatch(updateUser(values));
													closeModal();
												}}
												onClose={closeModal}
											/>);
									}}
								/>,
							},
							{
								Header: 'Fondo',
								accessor: 'fundName',
								Cell: props => <div>{props.original.fundName || "-"} <NTMButton primary outline
								                                                                onClick={() => edit(props.original)}>
									<i className={"fa fa-pencil-square"}/>
								</NTMButton></div>
							},
							{
								Header: 'Totale fatturato',
								accessor: 'totInvoices',
								Cell: props => <MoneyCell value={props.original.totInvoices}/>
							},
							{
								Header: 'Totale erogato',
								accessor: 'totOpenInvoices',
								Cell: props => <MoneyCell value={props.original.totOpenInvoices}/>
							}, {
								Header: 'Dettagli',
								Cell: props => <Link
									to={{
										pathname: `/shops/${props.original.id}`,
										state: {fromDashboard: true}
									}}
								>
									<i className="fa fa-external-link" aria-hidden="true"/>
								</Link>,
								filterable: false
							}
							]}
						filterable
						defaultPageSize={PAGE_SIZE}
						pageSizeOptions={PAGE_SIZE_OPTIONS}
						body={shopPage.content}
						manual
						pages={shopPage.totalPages}
						loading={isShopsFetching}
						onFetchData={state => setTableState({
							page: state.page,
							pageSize: state.pageSize,
							filtered: state.filtered || [],
							sorted: state.sorted || []
						})}
					/>
				</Col>
			</Row>
		</div>
	);

};
